import React from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatChartNumber } from '../../utils/numberFormat';

// Professional color palette (matching Analytics)
const CHART_COLORS = {
  primary: '#90BF45',      // CarbonCare green
  secondary: '#6C757D',    // Gray
  tertiary: '#17a2b8',     // Info blue
  transactions: '#1976d2', // Blue for transactions
  geolocations: '#2e7d32', // Green for geolocations
  shipments: '#ed6c02',    // Orange for shipments
};

export interface MonthlyChartData {
  month: string;
  monthNumber: number;
  year: number;
  transactions: number;
  geolocations: number;
  uniqueShipments: number;
}

export interface DailyChartData {
  day: string; // YYYY-MM-DD
  year: number;
  month: number;
  transactions: number;
  geolocations: number;
  uniqueShipments: number;
}

interface StatisticsChartProps {
  data: MonthlyChartData[];
  dailyData?: DailyChartData[];
  title?: string;
  height?: number;
  showLegend?: boolean;
}

type ChartType = 'bar' | 'line';
type MetricType = 'transactions' | 'geolocations' | 'shipments';
type GranularityType = 'monthly' | 'daily';

const MAX_DAILY_DAYS = 180; // ~6 months

interface SeriesFlags {
  showTransactions: boolean;
  showGeolocations: boolean;
  showShipments: boolean;
}

const SERIES_DEFS: { key: keyof MonthlyChartData; flag: keyof SeriesFlags; color: string; name: string }[] = [
  { key: 'transactions', flag: 'showTransactions', color: CHART_COLORS.transactions, name: 'Transactions' },
  { key: 'geolocations', flag: 'showGeolocations', color: CHART_COLORS.geolocations, name: 'Geolocations' },
  { key: 'uniqueShipments', flag: 'showShipments', color: CHART_COLORS.shipments, name: 'Unique Shipments' },
];

const renderBars = (flags: SeriesFlags) =>
  SERIES_DEFS.filter((s) => flags[s.flag]).map((s) => (
    <Bar key={s.key} dataKey={s.key} fill={s.color} name={s.name} radius={[4, 4, 0, 0]} />
  ));

const renderLines = (flags: SeriesFlags, showDaily: boolean) =>
  SERIES_DEFS.filter((s) => flags[s.flag]).map((s) => (
    <Line
      key={s.key}
      type="monotone"
      dataKey={s.key}
      stroke={s.color}
      strokeWidth={showDaily ? 2 : 3}
      name={s.name}
      dot={showDaily ? false : { fill: s.color, r: 4 }}
      activeDot={{ r: 6 }}
    />
  ));

const StatisticsChart: React.FC<StatisticsChartProps> = ({
  data,
  dailyData,
  title = 'Monthly Statistics Trend',
  height = 350,
  showLegend = true,
}) => {
  const theme = useTheme();
  const [chartType, setChartType] = React.useState<ChartType>('bar');
  const [metricType, setMetricType] = React.useState<MetricType | null>('transactions'); // default to transactions
  const [granularity, setGranularity] = React.useState<GranularityType>('monthly');

  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newChartType: ChartType | null,
  ) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  const handleMetricTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMetricType: MetricType | null,
  ) => {
    setMetricType(newMetricType); // null when same button re-clicked → shows all
  };

  const handleGranularityChange = (
    _event: React.MouseEvent<HTMLElement>,
    newGranularity: GranularityType | null,
  ) => {
    if (newGranularity !== null) {
      setGranularity(newGranularity);
    }
  };

  const chartProps = {
    margin: { top: 20, right: 30, left: 20, bottom: 20 },
  };

  // null metricType means show all; clicking selected button again deselects → shows all
  const showTransactions = !metricType || metricType === 'transactions';
  const showGeolocations = !metricType || metricType === 'geolocations';
  const showShipments = !metricType || metricType === 'shipments';

  // Check if daily data exceeds the limit
  const dailyExceedsLimit = dailyData && dailyData.length > MAX_DAILY_DAYS;
  const hasDailyData = dailyData && dailyData.length > 0;
  const showDaily = granularity === 'daily' && hasDailyData && !dailyExceedsLimit;

  // --- Monthly label logic ---
  const years = Array.from(new Set((data || []).map((d) => d.year)));
  const multiYear = years.length > 1;

  const labelMap = new Map<string, string>();
  (data || []).forEach((d) => {
    if (!labelMap.has(d.month)) {
      labelMap.set(d.month, multiYear ? `${d.month.slice(0, 3)} '${String(d.year).slice(2)}` : d.month);
    }
  });

  const parseDayString = (day: string): Date => {
    // Handle both "YYYY-MM-DD" and full ISO "2026-03-15T00:00:00.000Z"
    const str = String(day);
    const dateOnly = str.length > 10 ? str.slice(0, 10) : str;
    return new Date(dateOnly + 'T00:00:00');
  };

  const tooltipLabelFormatter = (label: React.ReactNode) => {
    const labelStr = typeof label === 'string' || typeof label === 'number' ? String(label) : '';
    if (showDaily) {
      try {
        const date = parseDayString(labelStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch {
        return labelStr;
      }
    }
    const entry = (data || []).find((d) => d.month === labelStr);
    return entry ? `${entry.month} ${entry.year}` : labelStr;
  };

  // --- Daily label logic ---
  const formatDailyTick = (day: string) => {
    try {
      const date = parseDayString(day);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return day;
    }
  };

  // Determine tick interval for daily view to avoid label crowding
  const getDailyTickInterval = () => {
    const totalDays = dailyData?.length || 0;
    if (totalDays <= 14) return 0; // show every day
    if (totalDays <= 31) return 1; // every other day
    if (totalDays <= 90) return 6; // weekly
    return 13; // bi-weekly
  };

  const activeData = showDaily ? dailyData : data;
  const dataKey = showDaily ? 'day' : 'month';

  const renderChart = () => {
    if (!activeData || activeData.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height={height}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      );
    }

    const tickFormatter = showDaily
      ? formatDailyTick
      : (v: string) => labelMap.get(v) ?? v;

    const xAxisProps: any = {
      dataKey,
      tickFormatter,
      tick: { fill: theme.palette.text.secondary, fontSize: showDaily ? 10 : 12 },
      axisLine: { stroke: alpha(theme.palette.divider, 0.3) },
      ...(showDaily ? { interval: getDailyTickInterval(), angle: -45, textAnchor: 'end', height: 60 } : {}),
    };

    const seriesFlags: SeriesFlags = { showTransactions, showGeolocations, showShipments };

    const yAxis = (
      <YAxis
        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
        axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
        tickFormatter={(value) => formatChartNumber(Number(value))}
      />
    );
    const tooltip = (
      <Tooltip
        contentStyle={{
          background: 'white',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
        formatter={(value: any) => [formatChartNumber(Number(value)), '']}
        labelFormatter={tooltipLabelFormatter}
      />
    );
    const grid = <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />;
    const legend = showLegend ? <Legend wrapperStyle={{ paddingTop: 10 }} /> : null;

    if (chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={activeData} {...chartProps}>
            {grid}
            <XAxis {...xAxisProps} />
            {yAxis}
            {tooltip}
            {legend}
            {renderBars(seriesFlags)}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    // Line chart
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={activeData} {...chartProps}>
          {grid}
          <XAxis {...xAxisProps} />
          {yAxis}
          {tooltip}
          {legend}
          {renderLines(seriesFlags, !!showDaily)}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          {hasDailyData && (
            <ToggleButtonGroup
              value={granularity}
              exclusive
              onChange={handleGranularityChange}
              size="small"
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="daily">Daily</ToggleButton>
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup
            value={metricType}
            exclusive
            onChange={handleMetricTypeChange}
            size="small"
          >
            <ToggleButton value="transactions">Transactions</ToggleButton>
            <ToggleButton value="geolocations">Geolocations</ToggleButton>
            <ToggleButton value="shipments">Shipments</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={handleChartTypeChange}
            size="small"
          >
            <ToggleButton value="bar">Bar</ToggleButton>
            <ToggleButton value="line">Line</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      {granularity === 'daily' && dailyExceedsLimit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Daily view is available for date ranges up to 6 months ({MAX_DAILY_DAYS} days).
          Your current selection has {dailyData.length} days. Please shorten the date range to view daily trends.
        </Alert>
      )}
      {renderChart()}
    </Paper>
  );
};

export default StatisticsChart;
